import React from 'react';

export const ResponsiveBreakpoint = {
  Small: 0,
  Medium: 1,
  Large: 2,
  XLarge: 3,
  XXLarge: 4,
  XXXLarge: 5,
} as const;

export type ResponsiveBreakpoint =
  (typeof ResponsiveBreakpoint)[keyof typeof ResponsiveBreakpoint];

const breakpointQueries: Record<ResponsiveBreakpoint, string> = {
  [ResponsiveBreakpoint.Small]: '(min-width: 320px) and (max-width: 479px)',
  [ResponsiveBreakpoint.Medium]: '(min-width: 480px) and (max-width: 639px)',
  [ResponsiveBreakpoint.Large]: '(min-width: 640px) and (max-width: 1023px)',
  [ResponsiveBreakpoint.XLarge]: '(min-width: 1024px) and (max-width: 1365px)',
  [ResponsiveBreakpoint.XXLarge]: '(min-width: 1366px) and (max-width: 1919px)',
  [ResponsiveBreakpoint.XXXLarge]: '(min-width: 1920px)',
};

const BreakpointContext = React.createContext<ResponsiveBreakpoint>(
  ResponsiveBreakpoint.Large,
);

function getInitialBreakpoint(): ResponsiveBreakpoint {
  for (const [key, query] of Object.entries(breakpointQueries)) {
    if (window.matchMedia(query).matches) {
      return Number(key) as ResponsiveBreakpoint;
    }
  }
  return ResponsiveBreakpoint.Large;
}

export const ResponsiveBreakpointProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [breakpoint, setBreakpoint] = React.useState<ResponsiveBreakpoint>(() =>
    getInitialBreakpoint(),
  );

  React.useEffect(() => {
    const mediaQueryLists = Object.entries(breakpointQueries).map(
      ([key, query]) => ({
        key: Number(key) as ResponsiveBreakpoint,
        mql: window.matchMedia(query),
      }),
    );

    const updateBreakpoint = () => {
      for (const { key, mql } of mediaQueryLists) {
        if (mql.matches) {
          setBreakpoint((prev) => (prev !== key ? key : prev));
          break;
        }
      }
    };

    mediaQueryLists.forEach(({ mql }) =>
      mql.addEventListener('change', updateBreakpoint),
    );
    updateBreakpoint();

    return () => {
      mediaQueryLists.forEach(({ mql }) =>
        mql.removeEventListener('change', updateBreakpoint),
      );
    };
  }, []);

  return (
    <BreakpointContext.Provider value={breakpoint}>
      {children}
    </BreakpointContext.Provider>
  );
};

export const useResponsiveBreakpoint = () =>
  React.useContext(BreakpointContext);

export const isAtLeast = (
  current: ResponsiveBreakpoint,
  target: ResponsiveBreakpoint,
): boolean => {
  return current >= target;
};
